import { FormControlLabel, Modal, Radio, RadioGroup } from "@mui/material";
import { cloneDeep, set } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CtaComponent from "../../../components/reusable-ui/cta";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { AGGREGATOR_FEATURES_MAP } from "../../../data/outlet-status-constants";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import {
  getSelfDeliveryChargeData,
  updateSelfDeliveryCharge,
} from "../../../redux/slices";
import { allowEverything, allowOnlyNumber } from "../../../utils";
import "./style.scss";

interface Props {
  onModalClose: any;
  moreOptionsMenuData: any;
  setApiStateModalData: any;
}

const SelfDeliveryCharge = (props: Props) => {
  const { onModalClose, moreOptionsMenuData, setApiStateModalData } = props;
  const ITEM_TYPE_MAP = {
    goods: {
      id: "goods",
      text: "Goods",
    },
    services: {
      id: "services",
      text: "Services",
    },
  };
  const { outletData } = moreOptionsMenuData;
  const [data, setData] = useState<any>({
    deliveryCharge: "",
    itemType: ITEM_TYPE_MAP.services.id,
    taxSlug: null,
    alwaysApplicable: 1,
    applicableBelowOrderAmount: "",
  });
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMsgMap, setErrMsgMap] = useState<any>({});
  const [selfDeliveryChargeApiData, setSelfDeliveryChargeApiData] =
    useState<any>({
      message: "",
      showMsg: true,
    });

  const dispatch = useDispatch();

  const TEXT_FIELD_CONFIG = {
    deliveryCharge: {
      label: "Charge Amount",
      placeholder: "Enter Charge Amount",
      regexCheck: allowOnlyNumber,
      customClass: "",
      parentClass: "mb-[6px]",
      value: data?.deliveryCharge,
      isMandatory: false,
      key: "deliveryCharge",
    },
    applicableBelowOrderAmount: {
      label: "Applicable Below Order Amount",
      placeholder: "Enter Charge Amount",
      regexCheck: allowOnlyNumber,
      customClass: "",
      parentClass: "",
      value: data?.applicableBelowOrderAmount,
      isMandatory: false,
      key: "applicableBelowOrderAmount",
    },
  };

  const DROPDOWN_CONFIGS = {
    itemType: {
      label: "Item Type",
      optionsMap: ITEM_TYPE_MAP,
      placeholder: "Select Item Type",
      uniqueKey: "id",
      dropdownHeading: "Item Types",
      searchPlaceholder: "Search a item type",
      parentClass: "w-[calc(50%-12px)] mr-[12px] sm:w-full sm:mr-0",
      key: "itemType",
      preSelectedValues: data?.itemType,
      onSubmit: (value: any) => handleInputChange(value[0], "itemType"),
      getListItemText: (data: any) => data?.text,
    },
    taxSlug: {
      label: "GST",
      optionsMap: {},
      placeholder: "Select GST",
      // uniqueKey: "id",
      dropdownHeading: "GST",
      searchPlaceholder: "Search a GST",
      parentClass: "w-[calc(50%-12px)] ml-[12px] sm:mb-[6px] sm:w-full sm:ml-0",
      key: "taxSlug",
      preSelectedValues: "",
      onSubmit: (value: any) => handleInputChange(value[0], "taxSlug"),
      // getListItemText: (data: any) => data?.text,
    },
  };

  useEffect(() => {
    let dataToPost = {
      brid: outletData?.brid,
      outletId: outletData?.id,
      type: outletData?.type,
    };
    const callback = (success: boolean, response: any) => {
      if (success) {
        setSelfDeliveryChargeApiData({ showMsg: false });
      } else if (response?.message) {
        setSelfDeliveryChargeApiData({
          message: response?.message,
          showMsg: true,
        });
      }
    };
    dispatch(getSelfDeliveryChargeData({ dataToPost, callback }));
  }, []);

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(data);
    set(dataToUpdate, fieldToUpdate, value);
    setData(dataToUpdate);
    setIsChangesMade(true);
  };

  const onSaveCtaClick = () => {
    // if (!data?.reason) {
    //   setErrMsgMap({ reason: "This field is mandatory." });
    //   return;
    // }
    // setErrMsgMap({});
    let dataToPost = {
      brid: outletData?.brid,
      outletId: outletData?.id,
      type: outletData?.type,
      data: {
        ...data,
        taxSlug: data?.taxSlug || null,
        deliveryCharge: data?.deliveryCharge || 0,
        applicableBelowOrderAmount: data?.applicableBelowOrderAmount || 0,
      },
    };
    const callback = (success: boolean, response: any) => {
      if (success) {
        onModalClose();
        setApiStateModalData({
          show: true,
          modalText: `${
            AGGREGATOR_FEATURES_MAP[moreOptionsMenuData?.selectedOptionId].text
          } Updated Successfully.`,
        });
      } else if (response?.message) {
        setSelfDeliveryChargeApiData({
          message: response?.message,
          showMsg: true,
        });
      }
    };
    dispatch(updateSelfDeliveryCharge({ dataToPost, callback }));
  };

  function renderTextField(textConfig: {
    label?: string;
    placeholder?: string;
    regexCheck?: any;
    onChange?: any;
    customClass?: string;
    parentClass?: string;
    value: string;
    key: string;
    isMandatory?: boolean;
    maxLength?: number;
  }) {
    return (
      <CustomTextField
        textBoxLabel={textConfig?.label || ""}
        placeholder={textConfig?.placeholder || ""}
        regexCheck={textConfig?.regexCheck || allowEverything}
        handleChange={(val: string) => handleInputChange(val, textConfig?.key)}
        customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal ${
          textConfig?.customClass || ""
        }`}
        parentClass={textConfig?.parentClass || ""}
        value={textConfig?.value}
        rows={1}
        isMandatory={!!textConfig?.isMandatory}
        maxLength={textConfig?.maxLength || 25}
        errorMessage={errorMsgMap[textConfig?.key] || ""}
      ></CustomTextField>
    );
  }

  function renderCustomDropDown(dropdownConfig: {
    label?: string;
    optionsMap: any;
    isMultiSelect?: boolean;
    placeholder?: string;
    getListItemText?: any;
    parentClass?: string;
    dropdownHeading?: string;
    searchPlaceholder?: string;
    onSubmit: any;
    preSelectedValues: any;
    uniqueKey?: any;
    key: string;
  }): any {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.label}
        optionsMap={dropdownConfig.optionsMap}
        isMultiSelect={dropdownConfig.isMultiSelect || false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={(data: any) =>
          dropdownConfig.getListItemText
            ? dropdownConfig.getListItemText(data)
            : data?.name || ""
        }
        parentClass={dropdownConfig.parentClass}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={dropdownConfig.onSubmit}
        preSelectedValues={dropdownConfig.preSelectedValues}
        uniqueKey={dropdownConfig.uniqueKey || "mid"}
        errorMessage={errorMsgMap[dropdownConfig.key] || ""}
      ></CustomDropdown>
    );
  }

  function renderJsonErrorMessage(jsonMsg: any) {
    return (
      <div
        className={`json-error-msg max-h-[516px] pb-[16px] px-[24px] mt-[28px]
      overflow-y-auto whitespace-pre-wrap text-14 sm:px-[16px] sm:mt-[12px]
    `}
      >
        {jsonMsg ? JSON.stringify(jsonMsg, null, 2) : null}
      </div>
    );
  }

  function renderAlwaysApplicableRadioGroup() {
    return (
      <div className="mb-[20px]">
        <p className="text-14 text-[#656565] font-normal">Always Applicable</p>
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          value={data?.alwaysApplicable}
          onChange={(e) =>
            handleInputChange(
              e.target.value === "1" ? 1 : 0,
              "alwaysApplicable"
            )
          }
          name="row-radio-buttons-group"
        >
          <FormControlLabel
            value={1}
            control={<Radio sx={{ fontSize: 12 }} />}
            label="Yes"
          />
          <FormControlLabel
            value={0}
            control={<Radio sx={{ fontSize: 12 }} />}
            label="No"
          />
        </RadioGroup>
      </div>
    );
  }

  function renderActionFooter() {
    return (
      <div
        className={`absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px]
          px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden
          sm:w-full sm:px-[16px]
        `}
      >
        <CtaComponent
          ctaText={"Discard"}
          ctaType={CTA_TYPE.secondary_cta}
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
          onCtaClick={onModalClose}
          customClass={"mr-10 sm:mr-[8px]"}
        ></CtaComponent>
        <CtaComponent
          ctaText={"Save"}
          ctaType={CTA_TYPE.active_cta}
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
          onCtaClick={onSaveCtaClick}
          customClass={"ml-10 sm:ml-[8px]"}
          isDisabled={!isChangesMade}
        ></CtaComponent>
      </div>
    );
  }

  function renderModalHeader() {
    return (
      <div
        className={`flex items-center justify-between py-[12px] px-[24px] border-b border-black/[0.15]
          sm:px-[16px]
        `}
      >
        <p className="text-22 font-semibold">
          {`${
            AGGREGATOR_FEATURES_MAP[moreOptionsMenuData?.selectedOptionId].text
          } - ${outletData?.channelDefName}`}
        </p>
        <div
          className="rista-sprite ic-cross-black"
          onClick={onModalClose}
        ></div>
      </div>
    );
  }

  return (
    <Modal open={true} onClose={onModalClose}>
      <div
        className={`self-delivery-charge-modal absolute w-[636px] rounded-[8px]
        overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white pb-[84px]
        sm:left-0 sm:right-0 sm:bottom-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
        sm:w-full sm:h-[90%] sm:rounded-b-[0]
      `}
      >
        {renderModalHeader()}
        {selfDeliveryChargeApiData?.showMsg ? (
          renderJsonErrorMessage(selfDeliveryChargeApiData?.message)
        ) : (
          <>
            <div className="px-[24px] mt-[20px] sm:px-[16px]">
              <div className="flex items-center mb-[6px] sm:flex-col">
                {renderCustomDropDown(DROPDOWN_CONFIGS.itemType)}
                {renderCustomDropDown(DROPDOWN_CONFIGS.taxSlug)}
              </div>
              {renderTextField(TEXT_FIELD_CONFIG.deliveryCharge)}
              {renderAlwaysApplicableRadioGroup()}
              {renderTextField(TEXT_FIELD_CONFIG.applicableBelowOrderAmount)}
            </div>
            {renderActionFooter()}
          </>
        )}
      </div>
    </Modal>
  );
};

export default SelfDeliveryCharge;
